import Big from 'big.js';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Tooltip } from 'common/components';
import { TooltipTriggerType } from 'common/components/Tooltip/Tooltip.component';

import { useBowser } from 'common/hooks';

import { memoizedComponent } from 'utils/memoizeComponent';
import { truncateNumber } from 'utils/truncateNumber';

import './index.css';

const INPUT_RANGE_POINTS = [0, 25, 50, 75, 100];

export const InputRange: React.FC<
  React.PropsWithChildren<{
    value: string | number;
    maxValue: string | number;
    step?: string | number;
    onChange: (value: string) => void;
    'data-test'?: string;
  }>
> = memoizedComponent(({ maxValue, step, value: externalValue, onChange, 'data-test': dataTest = 'input-range' }) => {
  const [value, setValue] = useState(externalValue);
  const [isSliding, setIsSliding] = useState(false);

  const { isMobile } = useBowser();

  const handleRangeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const val = event.target.value;
      setValue(val);
      onChange(val);
    },
    [onChange],
  );

  useEffect(
    function resetIfMaxValueChanged() {
      setValue('');
    },
    [maxValue],
  );

  useEffect(
    function syncExternalAmountChangeWithInternalValue() {
      if (externalValue <= maxValue) setValue(externalValue);
    },
    [externalValue, maxValue],
  );

  const calculatePercentage = useCallback(() => {
    if (!+value || !+maxValue) return 0;
    return new Big(Number(value)).div(Number(maxValue)).times(100).toNumber();
  }, [value, maxValue]);

  const getDotBackground = useCallback(
    (threshold: number) => calculatePercentage() >= threshold,
    [calculatePercentage],
  );

  const handlePercentageTooltipClick = useCallback(
    (percentage: number) => {
      const newValue = String((Number(maxValue) * percentage) / 100);
      console.log('newValue => ', newValue);
      setValue(newValue);
      onChange(newValue);
    },
    [maxValue, onChange],
  );

  const percentage = useMemo(() => truncateNumber(calculatePercentage(), 0, 'up'), [calculatePercentage]);

  const trigger: TooltipTriggerType = useMemo(() => (isMobile ? ['click'] : ['hover']), [isMobile]);
  const rangeStyle = useMemo(() => ({ width: `calc(100% - ${calculatePercentage()}%)` }), [calculatePercentage]);
  const circleStyle = useMemo(() => ({ left: `${calculatePercentage()}%` }), [calculatePercentage]);

  const onMouseDown = useCallback(() => setIsSliding(true), []);
  const onMouseUp = useCallback(() => setIsSliding(false), []);
  const onTouchMove = useCallback((event: React.TouchEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return (
    <div className='earn-products-range' data-body-scroll-lock-ignore>
      <div className='earn-products-range__layer-gradient' />
      <div className='earn-products-range__layer-grey' style={rangeStyle} />
      <div className='earn-products-range__layer-dots'>
        {INPUT_RANGE_POINTS.map((threshold) => (
          <Tooltip
            key={threshold}
            destroyTooltipOnHide
            trigger={trigger}
            overlay={`${threshold}%`}
            placement='top'
            enabled
          >
            <span
              className={`earn-products-range__layer-dot ${
                !getDotBackground(threshold) ? 'earn-products-range__layer-dot--transparent' : ''
              } ${threshold === Math.ceil(calculatePercentage()) ? 'earn-products-range__layer-dot--inactive' : ''}`}
              onClick={() => handlePercentageTooltipClick(threshold)}
            />
          </Tooltip>
        ))}
      </div>

      <input
        data-test={dataTest}
        className='earn-products-range__input'
        type='range'
        value={value || '0'}
        min='0'
        max={maxValue}
        step={step}
        onChange={handleRangeChange}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onTouchStart={onMouseDown}
        onTouchEnd={onMouseUp}
        onTouchMove={onTouchMove}
      />
      <div style={circleStyle} className='earn-products-range__circle'>
        {isSliding && <div className='earn-products-range__tooltip'>{percentage}%</div>}
      </div>
    </div>
  );
});
